import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { cn } from '../../lib/cn.ts';
import { formatInt } from '../../lib/format.ts';

export interface LiveNumberProps {
  value: number;
  /** Formats the value; default whole numbers, en-US ("1,204"). Use the shared formatters. */
  format?: (n: number) => string;
  className?: string;
}

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * A number that changes in place (motion.md M3): only the digits that changed roll, over
 * --duration-number on the settle curve. It never counts up from zero, the first render and
 * any change in length snap, and the width never shifts (tabular figures). Screen readers get
 * the plain text; the rolling columns are hidden from them. Under reduced motion the token is
 * 0 ms, so it snaps.
 *
 *   <LiveNumber value={needs.length} className="text-heading" />
 */
export function LiveNumber({ value, format = formatInt, className }: LiveNumberProps) {
  const text = format(value);
  // Only plain counts roll. Money, decimals and compact units (1.2M) change many times a minute;
  // rolling them reads as noise and catches the eye mid-flight, so they change in place.
  const rolls = /^[\d,]+$/.test(text);
  const [still, setStill] = useState(true);
  const prevLength = useRef(text.length);
  // The first paint and a change in the number of characters snap; later changes roll.
  useEffect(() => {
    if (prevLength.current !== text.length) {
      prevLength.current = text.length;
      setStill(true);
      return;
    }
    if (still) {
      const id = requestIdle(() => setStill(false));
      return () => cancelIdle(id);
    }
    return undefined;
  }, [text, still]);
  return (
    <span className={cn('relative inline-flex num', className)}>
      <span className="sr-only">{text}</span>
      <span aria-hidden className="inline-flex" data-still={still || !rolls || undefined}>
        {[...text].map((ch, i) => {
          const d = DIGITS.indexOf(ch);
          // Each character's place in the number, so a text gradient (prism-headline) spans the
          // whole figure: every glyph paints its own slice of the one gradient.
          const at = { '--ch-i': i, '--ch-n': text.length } as CSSProperties;
          if (d < 0) return <span key={`${i}${ch}`} data-ch="" style={at}>{ch}</span>;
          return (
            <span key={i} className="live-digit-cell" style={at}>
              <span className="invisible">0</span>
              <span className="live-digit" style={{ translate: `0 calc(${-d} * 1lh)` }}>
                {DIGITS.map((g) => (
                  <span key={g} className="live-digit-glyph" data-ch="">
                    {g}
                  </span>
                ))}
              </span>
            </span>
          );
        })}
      </span>
    </span>
  );
}

/** One frame later, so the snapped first render is painted before transitions switch on. */
function requestIdle(fn: () => void): number {
  return typeof window === 'undefined' ? 0 : window.setTimeout(fn, 0);
}
function cancelIdle(id: number): void {
  if (typeof window !== 'undefined') window.clearTimeout(id);
}
